import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiClient } from '../../lib/api';

export interface PartnerTenant {
  id: string;
  name: string;
  slug: string;
  effectiveFeatures: Array<'parts' | 'chemicals'>;
  assignedAt: string;
  expiresAt: string | null;
}

export interface DownloadRecord {
  id: string;
  tenantId: string;
  tenantName: string | null;
  reportType: string;
  reportMonth: string;
  fileFormat: string;
  fileName: string | null;
  rowCount: number | null;
  downloadedAt: string;
}

interface PartnerContextType {
  tenants: PartnerTenant[];
  tenantsLoading: boolean;
  tenantsError: string | null;
  history: DownloadRecord[];
  historyLoading: boolean;
  historyError: string | null;
  /** Re-fetch tenants and history */
  refresh: () => Promise<void>;
  /** Re-fetch history only (call after a download) */
  refreshHistory: () => Promise<void>;
}

const PartnerContext = createContext<PartnerContextType | undefined>(undefined);

export const usePartnerContext = (): PartnerContextType => {
  const ctx = useContext(PartnerContext);
  if (!ctx) {
    throw new Error('usePartnerContext must be used within a PartnerProvider');
  }
  return ctx;
};

/**
 * Convenience hook that mirrors the old usePartnerTenants API so existing
 * components need minimal changes — just swap the import.
 */
export const usePartnerTenants = () => {
  const { tenants, tenantsLoading: loading, tenantsError: error, refresh } = usePartnerContext();
  return { tenants, loading, error, refetch: refresh };
};

export const useDownloadHistory = () => {
  const { history, historyLoading: loading, historyError: error, refreshHistory } = usePartnerContext();
  return { history, loading, error, refetch: refreshHistory };
};

export const PartnerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenants, setTenants] = useState<PartnerTenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState<string | null>(null);

  const [history, setHistory] = useState<DownloadRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      setHistoryError(null);
      const response = await apiClient.get('/api/partner/reports/history?limit=10');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setHistory(data.history || []);
    } catch (err) {
      setHistoryError('Failed to load download history');
      console.error('Failed to load download history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchTenants = useCallback(async () => {
    try {
      setTenantsLoading(true);
      setTenantsError(null);
      const response = await apiClient.get('/api/partner/tenants');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setTenants(data.tenants || []);
    } catch (err) {
      setTenantsError('Failed to load assigned dealerships');
      console.error('Failed to load partner tenants:', err);
    } finally {
      setTenantsLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([fetchTenants(), fetchHistory()]);
  }, [fetchTenants, fetchHistory]);

  const refreshHistory = useCallback(async () => {
    await fetchHistory();
  }, [fetchHistory]);

  // Fetch once on mount
  useEffect(() => {
    fetchTenants();
    fetchHistory();
  }, [fetchTenants, fetchHistory]);

  const value: PartnerContextType = {
    tenants,
    tenantsLoading,
    tenantsError,
    history,
    historyLoading,
    historyError,
    refresh,
    refreshHistory,
  };

  return <PartnerContext.Provider value={value}>{children}</PartnerContext.Provider>;
};

export default PartnerProvider;
